import { useRef, useState, useCallback } from 'react';
import type { GlobalTableRef } from '@/types/table';

/**
 * 表格状态管理 Hook
 */
export const useTable = <T = any>() => {
  const tableRef = useRef<GlobalTableRef<T>>(null);
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<T[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  /**
   * 刷新表格
   */
  const refresh = useCallback(() => {
    tableRef.current?.refresh();
  }, []);

  /**
   * 清空选择
   */
  const clearSelection = useCallback(() => {
    tableRef.current?.clearSelection();
  }, []);

  /**
   * 获取选中行
   */
  const getSelectedRows = useCallback(() => {
    return tableRef.current?.getSelectedRows() || [];
  }, []);

  /**
   * 获取选中的 key
   */
  const getSelectedKeys = useCallback(() => {
    return tableRef.current?.getSelectedKeys() || [];
  }, []);

  /**
   * 更新数据源
   */
  const updateDataSource = useCallback((data: T[]) => {
    setDataSource(data);
  }, []);

  /**
   * 更新分页
   */
  const updatePagination = useCallback((page: number, pageSize: number, total?: number) => {
    setPagination((prev) => ({
      current: page,
      pageSize,
      total: total ?? prev.total,
    }));
  }, []);

  /**
   * 重置分页到第一页
   */
  const resetPagination = useCallback(() => {
    setPagination({
      current: 1,
      pageSize: 10,
      total: 0,
    });
  }, []);

  return {
    tableRef,
    loading,
    setLoading,
    dataSource,
    setDataSource,
    updateDataSource,
    pagination,
    setPagination,
    updatePagination,
    resetPagination,
    refresh,
    clearSelection,
    getSelectedRows,
    getSelectedKeys,
  };
};

